import React from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import useIsOnline from '../../hooks/useIsOnline/useIsOnline';
import useParticipantNetworkQualityLevel from '../../hooks/useParticipantNetworkQualityLevel/useParticipantNetworkQualityLevel';
import useRoomState from '../../hooks/useRoomState/useRoomState';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { useAppState } from '../../state';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.45rem',
    borderRadius: '999px',
    border: '1px solid rgba(136, 140, 142, 0.35)',
    background: 'rgba(245, 248, 255, 0.95)',
    padding: '0.3rem 0.65rem',
    whiteSpace: 'nowrap',
  },
  compact: {
    padding: '0.2rem 0.5rem',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '999px',
    flexShrink: 0,
  },
  label: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: 'rgb(40, 42, 43)',
    lineHeight: 1.1,
  },
  good: {
    background: theme.brand,
  },
  warning: {
    background: '#E46216',
  },
  error: {
    background: '#D61F1F',
  },
  neutral: {
    background: 'rgb(136, 140, 142)',
  },
}));

type ConnectionTone = 'good' | 'warning' | 'error' | 'neutral';

interface StatusDefinition {
  compactLabel: string;
  label: string;
  tone: ConnectionTone;
  title?: string;
}

function getConnectionStatus(
  isOnline: boolean,
  roomState: ReturnType<typeof useRoomState>,
  networkQualityLevel: number | null,
  roomType?: string
): StatusDefinition {
  if (!isOnline) {
    return {
      compactLabel: 'Offline',
      label: 'Sem internet',
      tone: 'error',
    };
  }

  if (roomState === 'reconnecting') {
    return {
      compactLabel: 'Recon.',
      label: 'Reconectando',
      tone: 'warning',
    };
  }

  if (roomType === 'go' || roomType === 'peer-to-peer') {
    return {
      compactLabel: 'Ativa',
      label: 'Conexao ativa',
      tone: 'good',
      title: 'A sala atual nao suporta a medicao Network Quality da Twilio.',
    };
  }

  if (typeof networkQualityLevel !== 'number') {
    return {
      compactLabel: 'Medindo',
      label: 'Verificando rede',
      tone: 'neutral',
      title: 'Aguardando a primeira medicao de qualidade de rede.',
    };
  }

  if (networkQualityLevel <= 2) {
    return {
      compactLabel: 'Lenta',
      label: 'Conexao lenta',
      tone: 'warning',
      title: 'A qualidade da conexao local esta baixa.',
    };
  }

  return {
    compactLabel: 'Boa',
    label: 'Conexao boa',
    tone: 'good',
    title: 'A qualidade da conexao local esta estavel.',
  };
}

export default function ConnectionStatusIndicator({ compact = false }: { compact?: boolean }) {
  const classes = useStyles();
  const { room } = useVideoContext();
  const { roomType } = useAppState();
  const isOnline = useIsOnline();
  const roomState = useRoomState();
  const networkQualityLevel = useParticipantNetworkQualityLevel(room?.localParticipant);

  if (!room) {
    return null;
  }

  const status = getConnectionStatus(isOnline, roomState, networkQualityLevel, roomType);
  const label = compact ? status.compactLabel : status.label;

  return (
    <div
      className={`${classes.container} ${compact ? classes.compact : ''}`.trim()}
      aria-live="polite"
      title={status.title || status.label}
    >
      <span className={`${classes.dot} ${classes[status.tone]}`}></span>
      <span className={classes.label}>{label}</span>
    </div>
  );
}
